// src/hooks/useBriefingAnalyses.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getBriefingAnalyses,
  deleteBriefingAnalysis,
  getBriefingAnalysisResults,
  type BriefingAnalysis,
  type BriefingAnalysisResults
} from '../api/briefing';
import type {
  UseBriefingAnalysesReturn
} from '../types/briefing';
import { dispatchTokenExpiredEvent } from '../utils/authEvents';

import { logger } from '../utils/logger';

export function useBriefingAnalyses(projectId?: string): UseBriefingAnalysesReturn {
  const [analyses, setAnalyses] = useState<BriefingAnalysis[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const fetchingRef = useRef(false);
  const pollingRef = useRef<Set<string>>(new Set()); // Track which analyses are currently being polled

  const { user } = useAuth();

  const handleTokenExpired = useCallback(() => {
    logger.warn('Token expired in briefing analyses hook, dispatching global event');
    dispatchTokenExpiredEvent();
  }, []);

  // Check authentication status
  useEffect(() => {
    setIsAuthenticated(!!user?.id);
  }, [user]);

  // Fetch analyses
  const fetchAnalyses = useCallback(async () => {
    // Don't make API calls if no user or no projectId
    if (!user?.id) {
      setIsAuthenticated(false);
      setAnalyses([]);
      return;
    }

    // Only fetch analyses when we have a specific projectId
    if (!projectId) {
      setAnalyses([]);
      setIsAuthenticated(true);
      return;
    }

    // Prevent duplicate calls if already fetching
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const fetchedAnalyses = await getBriefingAnalyses(
        projectId, 
        undefined, // status filter
        50, // limit
        user, 
        handleTokenExpired
      );
      
      // Filter out article analyses - they should only be shown within briefing
      // results. Include both v1 (combined) and 🆕 v2 (brief_v2) briefs.
      const briefingAnalyses = fetchedAnalyses.filter(analysis =>
        analysis.analysis_type === 'combined' || analysis.analysis_type === 'brief_v2'
      );
      
      if (process.env.NODE_ENV === 'development') {
        logger.log('Loaded analyses:', {
          total: fetchedAnalyses.length,
          briefing: briefingAnalyses.length,
          article: fetchedAnalyses.filter(a => a.analysis_type === 'article').length,
          filtered: briefingAnalyses.map(a => ({
            id: a.id,
            type: a.analysis_type,
            status: a.status,
            keyword: (a.input_data as any)?.keyword,
            error: a.error_message
          }))
        });
      }
      
      setAnalyses(briefingAnalyses);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch briefing analyses';
      setError(errorMessage);
      logger.error('Failed to fetch briefing analyses:', err);
      
      // If authentication error, clear analyses
      if (errorMessage.includes('Authentication') || errorMessage.includes('token')) {
        setIsAuthenticated(false);
        setAnalyses([]);
      }
    } finally {
      fetchingRef.current = false;
      setIsLoading(false);
    }
  }, [user, projectId, handleTokenExpired]);

  // Initial load and when projectId changes
  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  // ✅ Background polling for processing/pending analyses in list view
  useEffect(() => {
    const processingAnalyses = analyses.filter(a =>
      (a.status === 'processing' || a.status === 'pending') &&
      !a.id.startsWith('temp-') // ⬅️ FIX: Skip temporary optimistic IDs
    );

    if (processingAnalyses.length === 0) return;

    const pollInterval = setInterval(async () => {
      if (!user?.id) return;

      const { getBriefingAnalysisProgress } = await import('../api/briefing');

      for (const analysis of processingAnalyses) {
        try {
          const progress = await getBriefingAnalysisProgress(analysis.id, user, handleTokenExpired);

          // Update analysis with progress data
          setAnalyses(prev => prev.map(a =>
            a.id === analysis.id
              ? {
                  ...a,
                  status: progress.status as any,
                  progress_percentage: progress.progress_percentage,
                  current_step_name: progress.current_step_name
                }
              : a
          ));

          // If completed or failed, refresh to get final data
          if (progress.status === 'completed' || progress.status === 'failed') {
            fetchAnalyses();
          }
        } catch (error) {
          logger.error(`Failed to poll progress for ${analysis.id}:`, error);
        }
      }
    }, 5000); // Poll every 5 seconds for list view

    return () => clearInterval(pollInterval);
  }, [analyses, user, handleTokenExpired, fetchAnalyses]);

  // Delete analysis
  const deleteAnalysis = useCallback(async (analysisId: string): Promise<void> => {
    if (!user?.id) {
      throw new Error('Authentication required');
    }

    setError(null);
    
    try {
      await deleteBriefingAnalysis(analysisId, user, handleTokenExpired);
      setAnalyses(prev => prev.filter(analysis => analysis.id !== analysisId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete analysis';
      setError(errorMessage);
      throw err;
    }
  }, [user, handleTokenExpired]);

  // Get analysis results
  const getResults = useCallback(async (analysisId: string): Promise<BriefingAnalysisResults> => {
    // ⬅️ FIX: Reject temporary IDs
    if (analysisId.startsWith('temp-')) {
      throw new Error('Cannot get results for temporary analysis ID. Please wait for the analysis to be created.');
    }

    if (!user?.id) {
      throw new Error('Authentication required');
    }

    try {
      return await getBriefingAnalysisResults(analysisId, user, handleTokenExpired);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get analysis results';
      setError(errorMessage);
      throw err;
    }
  }, [user, handleTokenExpired]);

  // ✅ HYBRID POLLING: /progress (3s) + /results (10s) for incremental data
  const pollResults = useCallback(async (
    analysisId: string,
    onUpdate: (results: BriefingAnalysisResults) => void
  ): Promise<BriefingAnalysisResults> => {
    // ⬅️ FIX: Reject temporary IDs
    if (analysisId.startsWith('temp-')) {
      throw new Error('Cannot poll results for temporary analysis ID. Please wait for the analysis to be created.');
    }

    // Guard: Prevent duplicate polling for same analysis
    if (pollingRef.current.has(analysisId)) {
      logger.warn(`⚠️ Already polling analysis ${analysisId}, skipping duplicate call`);
      throw new Error('Already polling this analysis');
    }

    if (!user?.id) {
      throw new Error('Authentication required');
    }

    // Mark this analysis as being polled
    pollingRef.current.add(analysisId);

    try {
      const { getBriefingAnalysisProgress, getBriefingAnalysisResults } = await import('../api/briefing');

      // Get keyword from analyses state
      const analysis = analyses.find(a => a.id === analysisId);
      const keyword = (analysis?.input_data as any)?.keyword || '';

      // Track last results fetch time for 10s interval
      let lastResultsFetch = 0;
      let currentResults: BriefingAnalysisResults | null = null;
      let maxProgressSeen = 0; // ✅ Track highest progress to prevent resets
      let isFirstPoll = true; // ✅ Track if this is the first poll

      // Poll progress every 3 seconds
      const pollProgress = async (): Promise<BriefingAnalysisResults> => {
        // Check if we should stop polling
        if (!pollingRef.current.has(analysisId)) {
          logger.log(`🛑 Polling stopped for ${analysisId}`);
          throw new Error('Polling cancelled');
        }

        try {
          const now = Date.now();

          // ✅ FIRST POLL: Start with /progress (fast) then immediately fetch /results
          if (isFirstPoll) {
            isFirstPoll = false;

            // Step 1: Get progress first (fast, shows progress bar immediately)
            const progress = await getBriefingAnalysisProgress(analysisId, user, handleTokenExpired);
            maxProgressSeen = progress.progress_percentage ?? 0;

            // Create initial results with progress data
            currentResults = {
              analysis_id: analysisId,
              keyword: keyword,
              status: progress.status,
              analysis_type: 'combined',
              progress_percentage: maxProgressSeen,
              current_step: progress.current_step,
              current_step_name: progress.current_step_name,
              estimated_time_remaining_seconds: progress.estimated_time_remaining_seconds ?? undefined,
              steps_completed: progress.steps_completed,
              steps_remaining: progress.steps_remaining,
            };

            onUpdate(currentResults);

            if (process.env.NODE_ENV === 'development') {
              logger.log(`📊 ${maxProgressSeen}% - ${progress.current_step_name}`);
            }

            // Update analysis status in list
            setAnalyses(prev =>
              prev.map(a =>
                a.id === analysisId
                  ? { ...a, status: progress.status as any, progress_percentage: maxProgressSeen, current_step_name: progress.current_step_name }
                  : a
              )
            );

            // Check if already complete
            if (progress.status === 'completed') {
              const finalResults = await getBriefingAnalysisResults(analysisId, user, handleTokenExpired);
              pollingRef.current.delete(analysisId);
              return finalResults;
            } else if (progress.status === 'failed') {
              pollingRef.current.delete(analysisId);
              throw new Error('Analysis failed');
            }

            // Step 2: Immediately fetch /results to get any available data
            logger.log(`📦 Fetching initial results for ${analysisId}...`);
            const initialResults = await getBriefingAnalysisResults(analysisId, user, handleTokenExpired);
            lastResultsFetch = Date.now();

            // Merge with progress data (preserve maxProgressSeen)
            const newProgress = initialResults.progress_percentage ?? 0;
            maxProgressSeen = Math.max(maxProgressSeen, newProgress);

            currentResults = {
              ...currentResults,
              ...initialResults,
              progress_percentage: maxProgressSeen,
            };

            onUpdate(currentResults);

            if (process.env.NODE_ENV === 'development') {
              const availableComponents = Object.keys(currentResults.combined_analysis || {});
              logger.log(`📦 ${maxProgressSeen}% - Available: [${availableComponents.join(', ')}]`);
            }

            // Update analysis status in list
            setAnalyses(prev =>
              prev.map(a =>
                a.id === analysisId
                  ? {
                      ...a,
                      status: currentResults!.status as any,
                      progress_percentage: maxProgressSeen,
                      current_step_name: currentResults!.current_step_name
                    }
                  : a
              )
            );

            // Continue to next poll
            await new Promise(resolve => setTimeout(resolve, 3000));
            return pollProgress();
          }

          // Fetch /results every 10 seconds for incremental data
          if (now - lastResultsFetch >= 10000 || lastResultsFetch === 0) {
            logger.log(`📦 Fetching incremental results for ${analysisId}...`);
            const newResults = await getBriefingAnalysisResults(analysisId, user, handleTokenExpired);
            lastResultsFetch = now;

            // ✅ MERGE new results with current results (preserve highest progress)
            if (currentResults) {
              // Preserve the highest progress_percentage seen
              const newProgress = newResults.progress_percentage ?? 0;
              maxProgressSeen = Math.max(maxProgressSeen, newProgress);

              currentResults = {
                ...currentResults,           // Keep all existing data
                ...newResults,                // Merge new data (combined_analysis, etc.)
                progress_percentage: maxProgressSeen, // Use highest progress
              };
            } else {
              // First fetch
              currentResults = newResults;
              maxProgressSeen = newResults.progress_percentage ?? 0;
            }

            // Update analysis status and progress in list
            setAnalyses(prev =>
              prev.map(a =>
                a.id === analysisId
                  ? {
                      ...a,
                      status: currentResults!.status as any,
                      progress_percentage: maxProgressSeen,
                      current_step_name: currentResults!.current_step_name
                    }
                  : a
              )
            );

            // Send full results update (includes incremental combined_analysis data)
            onUpdate(currentResults);

            if (process.env.NODE_ENV === 'development') {
              const availableComponents = Object.keys(currentResults.combined_analysis || {});
              logger.log(`📦 ${currentResults.progress_percentage || 0}% - Available: [${availableComponents.join(', ')}]`);
            }

            // Check if complete
            if (currentResults.status === 'completed') {
              logger.log(`✅ Analysis ${analysisId} completed!`);

              setAnalyses(prev =>
                prev.map(a =>
                  a.id === analysisId
                    ? { ...a, status: currentResults!.status as any, completed_at: currentResults!.processed_at }
                    : a
                )
              );

              pollingRef.current.delete(analysisId);
              return currentResults;
            } else if (currentResults.status === 'failed') {
              logger.error(`❌ Analysis ${analysisId} failed`);
              pollingRef.current.delete(analysisId);
              throw new Error('Analysis failed');
            }
          } else {
            // Between /results calls, use lightweight /progress for updates
            const progress = await getBriefingAnalysisProgress(analysisId, user, handleTokenExpired);

            // ✅ Update maxProgressSeen (never go backwards!)
            const newProgress = progress.progress_percentage ?? 0;
            maxProgressSeen = Math.max(maxProgressSeen, newProgress);

            if (process.env.NODE_ENV === 'development') {
              logger.log(`📊 ${maxProgressSeen}% - ${progress.current_step_name}`);
            }

            // Update analysis status and progress in list
            setAnalyses(prev =>
              prev.map(a =>
                a.id === analysisId
                  ? {
                      ...a,
                      status: progress.status as any,
                      progress_percentage: maxProgressSeen,
                      current_step_name: progress.current_step_name
                    }
                  : a
              )
            );

            // Merge progress with current results (preserve all data)
            if (currentResults) {
              currentResults = {
                ...currentResults,
                progress_percentage: maxProgressSeen, // ✅ Use highest progress seen
                current_step: progress.current_step,
                current_step_name: progress.current_step_name,
                estimated_time_remaining_seconds: progress.estimated_time_remaining_seconds ?? undefined,
                steps_completed: progress.steps_completed,
                steps_remaining: progress.steps_remaining,
                status: progress.status,
              };

              onUpdate(currentResults);
            } else {
              // First progress update before any results
              const optimisticResults: BriefingAnalysisResults = {
                analysis_id: analysisId,
                keyword: keyword,
                status: progress.status,
                analysis_type: 'combined',
                progress_percentage: maxProgressSeen, // ✅ Use highest progress seen
                current_step: progress.current_step,
                current_step_name: progress.current_step_name,
                estimated_time_remaining_seconds: progress.estimated_time_remaining_seconds ?? undefined,
                steps_completed: progress.steps_completed,
                steps_remaining: progress.steps_remaining,
              };

              onUpdate(optimisticResults);
            }

            // Check if complete or failed
            if (progress.status === 'completed' || progress.status === 'failed') {
              // Force immediate /results fetch
              lastResultsFetch = 0;
            }
          }

          // Continue polling after 3 seconds
          await new Promise(resolve => setTimeout(resolve, 3000));
          return pollProgress();

        } catch (error) {
          logger.error('❌ Error polling:', error);
          pollingRef.current.delete(analysisId);
          throw error;
        }
      };

      return await pollProgress();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to poll analysis results';
      setError(errorMessage);

      // Remove from polling set
      pollingRef.current.delete(analysisId);

      // Update analysis status to failed (only if not cancelled)
      if (!errorMessage.includes('cancelled') && !errorMessage.includes('Already polling')) {
        setAnalyses(prev =>
          prev.map(analysis =>
            analysis.id === analysisId
              ? { ...analysis, status: 'failed', error_message: errorMessage }
              : analysis
          )
        );
      }

      throw err;
    }
  }, [user, handleTokenExpired, analyses]);

  // Refresh analyses
  const refreshAnalyses = useCallback(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  // Stop polling for a specific analysis
  const stopPolling = useCallback((analysisId: string) => {
    if (pollingRef.current.has(analysisId)) {
      logger.log(`🛑 Stopping polling for ${analysisId}`);
      pollingRef.current.delete(analysisId);
    }
  }, []);

  // Cleanup: Stop all polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollingRef.current.size > 0) {
        logger.log(`🧹 Cleaning up ${pollingRef.current.size} polling instances`);
        pollingRef.current.clear();
      }
    };
  }, []);

  return {
    analyses,
    isLoading,
    error,
    deleteAnalysis,
    refreshAnalyses,
    getResults,
    pollResults,
    stopPolling,
    isAuthenticated
  };
}